import {
  MoreVert as MenuIcon,
  OpenInNewRounded as OpenIcon,
  RefreshRounded as RefreshIcon,
} from "@mui/icons-material";
import {
  Box,
  Card,
  CardContent,
  CircularProgress,
  Menu,
  MenuItem,
  Select,
  Stack,
  ToggleButton,
  ToggleButtonGroup,
  Tooltip,
  Typography,
} from "@mui/material";
import { readableDate } from "common/dates";
import Component from "components/Component";
import Button from "components/common/Button";
import IconButton from "components/common/IconButton";
import { useSnackbar } from "notistack";
import { useCallback, useEffect, useState } from "react";
import { useListCompaniesQuery } from "services/adminService";
import {
  useGetCompanySubmissionCacheStatsQuery,
  useTriggerMaterializationMutation,
} from "services/materializeService";

const enDashChar = "\u2013";

const KB = 1024;
const MB = 1024 * 1024;

const HISTOGRAM_BUCKETS = [
  { label: `0${enDashChar}20 KB`, shortLabel: "20", max: 20 * KB },
  { label: `20${enDashChar}40 KB`, shortLabel: "40", max: 40 * KB },
  { label: `40${enDashChar}60 KB`, shortLabel: "60", max: 60 * KB },
  { label: `60${enDashChar}80 KB`, shortLabel: "80", max: 80 * KB },
  { label: `80${enDashChar}100 KB`, shortLabel: "100", max: 100 * KB },
  { label: `100${enDashChar}120 KB`, shortLabel: "120", max: 120 * KB },
  { label: `120${enDashChar}140 KB`, shortLabel: "140", max: 140 * KB },
  { label: `140${enDashChar}160 KB`, shortLabel: "160", max: 160 * KB },
  { label: `160${enDashChar}180 KB`, shortLabel: "180", max: 180 * KB },
  { label: `180${enDashChar}200 KB`, shortLabel: "200", max: 200 * KB },
  { label: `200${enDashChar}400 KB`, shortLabel: "400", max: 400 * KB },
  { label: `400${enDashChar}600 KB`, shortLabel: "600", max: 600 * KB },
  { label: `600${enDashChar}800 KB`, shortLabel: "800", max: 800 * KB },
  { label: `800 KB${enDashChar}1 MB`, shortLabel: "1MB", max: MB },
  { label: "1 MB+", shortLabel: "1MB+", max: Infinity },
];

const UUID_SUFFIX = /-[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i;
const formatCompanyId = (id) => id.replace(UUID_SUFFIX, "").toUpperCase();

const BAR_COLOR = "primary.main";
const BAR_AREA_HEIGHT = 80;
const LABEL_HEIGHT = 14;
const MIN_BAR_HEIGHT = 3;

const buildHistogram = (files) => {
  const counts = HISTOGRAM_BUCKETS.map(() => 0);
  for (const { size } of files) {
    const i = HISTOGRAM_BUCKETS.findIndex((b) => size < b.max);
    if (i !== -1) counts[i]++;
  }
  return HISTOGRAM_BUCKETS.map((b, i) => ({ ...b, count: counts[i] }));
};

const formatBytes = (bytes) => {
  if (bytes < KB) return `${bytes} B`;
  if (bytes < MB) return `${(bytes / KB).toFixed(1)} KB`;
  return `${(bytes / MB).toFixed(1)} MB`;
};

// Invisible — calls the hook for one company and reports the result up to the parent.
const CompanyStatsFetcher = ({ companyId, onResult }) => {
  const result = useGetCompanySubmissionCacheStatsQuery(companyId);
  useEffect(() => {
    onResult(companyId, result);
  }, [companyId, result, onResult]);
  return null;
};

const Histogram = ({ files, maxCount }) => {
  const isEmpty = !files?.length;
  const histogram = buildHistogram(isEmpty ? [] : files);
  const localMax = isEmpty ? 1 : Math.max(...histogram.map((b) => b.count), 1);
  const effectiveMax = maxCount ?? localMax;
  const totalBytes = isEmpty ? 0 : files.reduce((s, f) => s + f.size, 0);
  const avgSize = isEmpty ? null : formatBytes(totalBytes / files.length);

  return (
    <Box sx={{ mt: 2 }}>
      <Stack
        direction={"row"}
        alignItems={"flex-end"}
        sx={{ height: BAR_AREA_HEIGHT, gap: "2px" }}
      >
        {histogram.map((bucket) => {
          const heightPct = (bucket.count / effectiveMax) * 100;
          return (
            <Tooltip
              key={bucket.label}
              title={`${bucket.label}: ${bucket.count}`}
              arrow
            >
              <Box
                sx={{
                  flex: 1,
                  height: `${heightPct}%`,
                  minHeight: bucket.count > 0 ? MIN_BAR_HEIGHT : 0,
                  backgroundColor: BAR_COLOR,
                  borderRadius: "2px 2px 0 0",
                  cursor: "default",
                  transition: "height 0.3s ease",
                  "&:hover": { opacity: 0.8 },
                }}
              />
            </Tooltip>
          );
        })}
      </Stack>
      <Stack direction={"row"} sx={{ gap: "2px", height: LABEL_HEIGHT }}>
        {histogram.map((bucket) => (
          <Stack
            key={bucket.label}
            flex={1}
            alignItems={"center"}
            justifyContent={"flex-start"}
            sx={{ pt: "3px" }}
          >
            <Typography
              variant={"caption"}
              color={"text.secondary"}
              sx={{ fontSize: "0.6rem", lineHeight: 1.2, textAlign: "center" }}
            >
              {bucket.shortLabel}
            </Typography>
          </Stack>
        ))}
      </Stack>
      <Typography
        variant={"caption"}
        color={"text.secondary"}
        sx={{ fontSize: "0.6rem", display: "block", textAlign: "center" }}
      >
        {avgSize
          ? `File size distribution (KB) · Avg. ${avgSize}`
          : "File size distribution (KB)"}
      </Typography>
    </Box>
  );
};

const LastModifiedLabel = ({ files }) => {
  if (!files?.length) return null;

  const timestamps = files.map((f) => new Date(f.lastModified).getTime());
  const maxTs = Math.max(...timestamps);

  return (
    <Typography
      variant={"caption"}
      color={"text.secondary"}
      sx={{ textAlign: "right", ml: 1 }}
    >
      {`Last updated ${readableDate(maxTs, { includeTime: true })}`}
    </Typography>
  );
};

const CardMenu = ({ companyId, label, onRefetch }) => {
  const [anchor, setAnchor] = useState(null);
  const [
    triggerMaterialization,
    { isLoading },
  ] = useTriggerMaterializationMutation();
  const { enqueueSnackbar } = useSnackbar();

  const handleCopyId = async () => {
    setAnchor(null);
    await navigator.clipboard.writeText(companyId);
    enqueueSnackbar(`Copied ${label} company ID`, { variant: "success" });
  };

  const handleTriggerMaterialization = async () => {
    setAnchor(null);
    const result = await triggerMaterialization({ companyId });
    if (result.data?.message === "Service Unavailable") {
      enqueueSnackbar(`${label} timed out — materialisation is still running`, {
        variant: "warning",
      });
    } else if (result.data?.status === "success") {
      enqueueSnackbar(`${label} materialisation completed`, {
        variant: "success",
      });
      onRefetch?.();
    } else {
      enqueueSnackbar(`${label} materialisation failed`, { variant: "error" });
    }
  };

  return (
    <>
      {isLoading ? (
        <Box sx={{ p: "8px", display: "flex" }}>
          <CircularProgress size={20} />
        </Box>
      ) : (
        <IconButton
          icon={MenuIcon}
          scale={"small"}
          onClick={(e) => {
            e.stopPropagation();
            setAnchor(e.currentTarget);
          }}
        />
      )}
      <Menu
        anchorEl={anchor}
        open={Boolean(anchor)}
        onClose={() => setAnchor(null)}
        onClick={(e) => e.stopPropagation()}
      >
        <MenuItem onClick={handleCopyId}>{"Copy company ID"}</MenuItem>
        <MenuItem onClick={handleTriggerMaterialization}>
          {"Trigger materialisation"}
        </MenuItem>
      </Menu>
    </>
  );
};

const CompanyStatsCard = ({
  label,
  companyId,
  data,
  isLoading,
  isError,
  maxCount,
  onRefetch,
  cardSx = {},
}) => {
  const handleClick = companyId
    ? () => window.open(`/admin/${companyId}`, "_blank")
    : undefined;

  return (
    <Card
      variant={"outlined"}
      onClick={handleClick}
      sx={{
        height: "100%",
        cursor: companyId ? "pointer" : "default",
        transition: "border-color 0.15s",
        bgcolor: data?.fileCount === 0 ? "grey.50" : null,
        "&:hover": companyId ? { borderColor: "primary.main" } : {},
        "&:hover .company-label": companyId ? { color: "primary.main" } : {},
        "&:hover .open-icon": companyId ? { opacity: 1 } : {},
        ...cardSx,
      }}
    >
      <CardContent>
        <Stack
          direction={"row"}
          justifyContent={"space-between"}
          alignItems={"center"}
          sx={{ mb: 0.5 }}
        >
          <Stack direction={"row"} spacing={1} alignItems={"center"}>
            <Typography
              className={"company-label"}
              variant={"caption"}
              color={"text.secondary"}
              sx={{ fontFamily: "monospace", fontWeight: 600 }}
            >
              {label}
            </Typography>
            <Box
              className={"open-icon"}
              sx={{ opacity: 0, transition: "100ms" }}
            >
              <OpenIcon fontSize={"12px"} />
            </Box>
          </Stack>
          <Stack direction={"row"} alignItems={"center"} spacing={0.5}>
            {!isLoading && <LastModifiedLabel files={data?.files} />}
            {companyId && (
              <CardMenu
                companyId={companyId}
                label={label}
                onRefetch={onRefetch}
              />
            )}
          </Stack>
        </Stack>

        {isLoading && (
          <Stack
            justifyContent={"center"}
            alignItems={"center"}
            sx={{ height: 160 }}
          >
            <CircularProgress size={32} />
          </Stack>
        )}

        {isError && (
          <Typography variant={"body2"} color={"error"}>
            {"Failed to load"}
          </Typography>
        )}

        {!isLoading && !isError && data && (
          <>
            <Stack direction={"row"} spacing={5} alignItems={"baseline"}>
              <Stack alignItems={"center"}>
                <Typography variant={"h3"} fontWeight={"bold"} lineHeight={1}>
                  {data.fileCount.toLocaleString()}
                </Typography>
                <Typography variant={"caption"} color={"text.secondary"}>
                  {"Submissions"}
                </Typography>
              </Stack>
              <Stack alignItems={"center"}>
                <Typography variant={"h3"} fontWeight={"bold"} lineHeight={1}>
                  {Math.max(0.1, data.totalBytes / MB).toFixed(1)}
                </Typography>
                <Typography variant={"caption"} color={"text.secondary"}>
                  {"Size (MB)"}
                </Typography>
              </Stack>
            </Stack>
            <Histogram files={data.files} maxCount={maxCount} />
          </>
        )}
      </CardContent>
    </Card>
  );
};

const SCALE_LOCAL = "local";
const SCALE_GLOBAL = "global";

const SORT_SUBMISSIONS = "submissions";
const SORT_SIZE = "size";
const SORT_COMPANY = "company";

const CARD_WIDTH = {
  xs: "100%",
  md: "calc(50% - 8px)",
  lg: "calc(33.333% - 11px)",
  xl: "calc(25% - 12px)",
};

const CompanyStats = () => {
  const {
    data: companies,
    isLoading: companiesLoading,
  } = useListCompaniesQuery();
  const [results, setResults] = useState({});
  const [scale, setScale] = useState(SCALE_GLOBAL);
  const [sortOrder, setSortOrder] = useState(SORT_SUBMISSIONS);
  const [refreshTime, setRefreshTime] = useState(0);

  const handleResult = useCallback((companyId, result) => {
    setResults((prev) => ({
      ...prev,
      [companyId]: {
        ...result,
        fetchedAt: result.isFetching
          ? prev[companyId]?.fetchedAt ?? 0
          : Date.now(),
      },
    }));
  }, []);

  const handleRefresh = () => {
    setRefreshTime(Date.now());
    Object.values(results).forEach((r) => r.refetch?.());
  };

  const sortedCompanies = [...(companies ?? [])].sort((a, b) => {
    if (sortOrder === SORT_COMPANY) {
      return formatCompanyId(a.id).localeCompare(formatCompanyId(b.id));
    }
    const aData = results[a.id]?.data ?? null;
    const bData = results[b.id]?.data ?? null;
    if (aData !== null && bData !== null) {
      const aVal = sortOrder === SORT_SIZE ? aData.totalBytes : aData.fileCount;
      const bVal = sortOrder === SORT_SIZE ? bData.totalBytes : bData.fileCount;
      if (bVal !== aVal) return bVal - aVal;
      return formatCompanyId(a.id).localeCompare(formatCompanyId(b.id));
    }
    if (aData !== null) return -1;
    if (bData !== null) return 1;
    return formatCompanyId(a.id).localeCompare(formatCompanyId(b.id));
  });

  const allFiles = Object.values(results).flatMap((r) =>
    (r.fetchedAt ?? 0) >= refreshTime ? r.data?.files ?? [] : []
  );
  const totalData = {
    fileCount: allFiles.length,
    totalBytes: allFiles.reduce((s, f) => s + f.size, 0),
    files: allFiles,
  };

  const globalMax =
    totalData.files.length > 0
      ? Math.max(...buildHistogram(totalData.files).map((b) => b.count), 1)
      : 1;

  return (
    <>
      {sortedCompanies.map(({ id }) => (
        <CompanyStatsFetcher key={id} companyId={id} onResult={handleResult} />
      ))}
      <Component
        title={"Submission Cache Stats"}
        options={
          <Stack direction={"row"} alignItems={"center"} spacing={1}>
            <Typography variant={"body2"} color={"text.secondary"}>
              {"Sort by"}
            </Typography>
            <Select
              value={sortOrder}
              onChange={(e) => setSortOrder(e.target.value)}
              size={"small"}
              sx={{ fontSize: "0.75rem" }}
            >
              <MenuItem value={SORT_SUBMISSIONS}>{"Submission Count"}</MenuItem>
              <MenuItem value={SORT_SIZE}>{"Storage Size"}</MenuItem>
              <MenuItem value={SORT_COMPANY}>{"Company Name"}</MenuItem>
            </Select>
            <ToggleButtonGroup
              value={scale}
              exclusive
              size={"small"}
              onChange={(_, v) => v && setScale(v)}
              sx={{
                "& .MuiToggleButton-root": {
                  py: "4px",
                  px: "8px",
                  fontSize: "0.75rem",
                },
              }}
            >
              <ToggleButton value={SCALE_GLOBAL}>{"Shared scale"}</ToggleButton>
              <ToggleButton value={SCALE_LOCAL}>{"Per company"}</ToggleButton>
            </ToggleButtonGroup>
            <Button
              startIcon={<RefreshIcon />}
              size={"small"}
              onClick={handleRefresh}
            >
              {"Refresh"}
            </Button>
          </Stack>
        }
      >
        {companiesLoading ? (
          <Stack alignItems={"center"} sx={{ mt: 4 }}>
            <CircularProgress />
          </Stack>
        ) : (
          <Stack direction={"row"} flexWrap={"wrap"} gap={2} useFlexGap>
            <Box sx={{ width: CARD_WIDTH }}>
              <CompanyStatsCard
                label={"ALL COMPANIES"}
                data={totalData}
                isLoading={false}
                isError={false}
                maxCount={null}
                cardSx={{ bgcolor: "grey.50" }}
              />
            </Box>
            {sortedCompanies.map(({ id }) => {
              const result = results[id] ?? {};
              return (
                <Box key={id} sx={{ width: CARD_WIDTH }}>
                  <CompanyStatsCard
                    label={formatCompanyId(id)}
                    companyId={id}
                    data={result.data}
                    isLoading={
                      (result.isLoading ?? true) || (result.isFetching ?? false)
                    }
                    isError={result.isError ?? false}
                    maxCount={scale === SCALE_GLOBAL ? globalMax : null}
                    onRefetch={result.refetch}
                  />
                </Box>
              );
            })}
          </Stack>
        )}
      </Component>
    </>
  );
};

export default CompanyStats;
